import React, { useEffect, useMemo } from "react";

import { AccommodationBlockForm } from "Bands/components/AccommodationBlockForm/AccommodationBlockForm";
import { useEventContext } from "Bands/contexts";
import { BAND_PATHS } from "Bands/router";
import { clearActionMeta, dataMetaSelector } from "Bands/state";
import { editEventAccommodationBlock } from "Bands/state/data/actions/eventBlocks/eventAccommodationBlock";
import { dispatchGetEventAccommodationRoomTypes } from "Bands/state/data/actions/eventBlocks/eventAccommodationRoomType";
import {
  BAND_ACTIONS,
  EventBlocks,
  IAccommodationBlock,
  IAccommodationBlockParams,
} from "Bands/types";
import { useClearActionMetaOnUnMount, useEventRoute } from "Bands/utils";
import { format } from "date-fns";
import { omit } from "lodash-es";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { copy } from "shared/assets";
import { Button } from "shared/components";
import { addTemporaryToast } from "shared/components/Toast";
import { Spacer } from "shared/layout";
import { IAPIState, IFormButtons, IStatus, ITheme } from "shared/types";
import { addDays } from "shared/utils";
import { EditAccommodationReservation } from "../EditAccommodationReservation/EditAccommodationReservation";
import { EditAccommodationRooms } from "../EditAccommodationRooms/EditAccommodationRooms";
import CopyBlockModal from "Bands/pages/copy-block/CopyBlockModal";

const { accommodationBlock: blockCopy } = copy;

interface Props {
  block: IAccommodationBlock;
}

export const EditAccommodationBlockForm: React.FC<Props> = ({ block }) => {
  const { event, eventId } = useEventContext();
  const { pushRoute } = useEventRoute();
  const { blockId: id } = useParams<{ blockId: string }>();
  const dispatch = useDispatch();

  const { api: apiMeta } = useSelector(dataMetaSelector(BAND_ACTIONS.editAccommodationBlock));

  useClearActionMetaOnUnMount(BAND_ACTIONS.editAccommodationBlock);

  const buttons: IFormButtons = useMemo(
    () => ({
      primary: {
        children: blockCopy.edit.saveAccommodation,
        "data-testid": "accommodation_block_submit",
      },
    }),
    []
  );

  const handleSubmit = (data: IAccommodationBlockParams) => {
    if (!eventId) return;

    dispatch(
      editEventAccommodationBlock({
        id,
        data,
      })
    );
  };

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      dispatch(clearActionMeta(BAND_ACTIONS.editAccommodationBlock));

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: blockCopy.edit.success,
        })
      );
    }

    dispatchGetEventAccommodationRoomTypes(dispatch, id);
  }, [apiMeta, dispatch, id]);

  const defaultValues = useMemo(() => {
    if (!event?.date) return;

    return omit(
      {
        ...block,
        ...(!block.checkInDate && { checkInDate: event?.date }),
        ...(!block.checkOutDate && {
          checkOutDate: format(addDays(event?.date, 1), "Y-MM-dd"),
        }),
      },
      "event"
    );
  }, [block, event?.date]);

  return (
    <Spacer size="l">
      <CopyBlockModal blockType={EventBlocks.accommodation} deleteBlockId={block.id} />
      <AccommodationBlockForm
        defaultValues={defaultValues}
        buttons={buttons}
        handleSubmit={handleSubmit}
        apiStatus={apiMeta?.status}
        apiError={apiMeta?.error}
        error={!!apiMeta?.error}
      />

      <EditAccommodationRooms block={block} />

      <EditAccommodationReservation block={block} />

      <Button onClick={() => pushRoute(BAND_PATHS.BAND_EVENT)} theme={ITheme.secondary}>
        {copy.form.finish}
      </Button>
    </Spacer>
  );
};
